import Tabs from "@/common/components/Tabs";
import { defaultTabs } from "../../../constants/TmaisUsersShowTabs.constant";
import { ShowHeader } from "@/common/components/ShowHeader";
import { useParams } from "react-router-dom";
import useTmaisUser from "../../../hooks/useTmaisUser";
import { TmaisUserData } from "../../../types/tmaisUsers.type";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useUpdateTmaisUser from "@/features/tmaisUsers/hooks/useUpdateTmaisUser";
import { SubmitHandler, useForm } from "react-hook-form";
import { TmaisUsersValidationSchema, updateSchema } from "@/features/tmaisUsers/schemas/update.schema";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import Button from "@/common/components/Button";
import { useGoBack } from "@/common/hooks/useGoBack";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormTransformers } from "@/common/utils/formTransformers";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";

const UsersUpdate = () => {
  const {id} = useParams()

  const queryClient = useQueryClient()
  const goBack = useGoBack()
  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [file, setFile] = useState<Blob>();

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasUpdateTmaisUserPermission = !isUserPermissionsPending ? hasUserPermission('Admin::Tmais::Users::Update') : true

  const {
    data: tmaisUser
  } = useTmaisUser(String(id))

  const {
    mutate: updateTmaisUser
  } = useUpdateTmaisUser({
    id: String(id),
    file,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['tmaisUser']})
      goBack({fallback: `/tmais-users/${tmaisUser?.id}`})
    },
    onError: (error) => {
      FormTransformers.errorsTransformer<TmaisUsersValidationSchema>({ setError })(error)
      if (error.response?.data.errors.base) {
        setIsError(true);
        setMessages(error.response?.data.errors.base);
      }
    }
  })

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<TmaisUsersValidationSchema>({
    resolver: zodResolver(updateSchema)
  })

  const onSubmit:SubmitHandler<TmaisUsersValidationSchema> = (data) => updateTmaisUser(data)

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasUpdateTmaisUserPermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <ShowHeader
          goBack
          goBackProps={{
            fallback: `/tmais-users/${id}`,
            searchLastRouteFromFallback: true,
          }}
          title="Clientes"
          subtitle={(
            <h2 className="text-white-800">
              {tmaisUser?.full_name}
            </h2>
          )}
          buttons={
            <Button
              name="submit"
              type="submit"
            >
              Salvar
            </Button>
          }
        />

        <Tabs
          tabsListClassName="flex h-auto flex-row flex-wrap justify-start w-full bg-transparent rounded-none"
          tabsTriggerClassName="flex-1 h-8 transition-none duration-0 rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
          tabsContentClassName="mt-6"
          tabsList={defaultTabs({
            tmaisUser: tmaisUser as TmaisUserData,
            updatePage: true,
            register,
            setFile,
            file,
            errors
          })}
        />

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError={isError}
          />
        )}
      </form>
    </ErrorMessageWrapper>
  );
};

 export default UsersUpdate;
